import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'

// Q15 Paired statements from topline report - ordered by Statement A support (highest first)
const PAIRED_STATEMENTS = [
  {
    id: 'Paired_OwnView0',
    statementA: 'Indigenous communities should NOT have more say than anyone else about land use and development in their traditional territories.',
    statementB: 'Indigenous communities should have final say about land use and development in their traditional territories.',
  },
  {
    id: 'Paired_OwnView1',
    statementA: 'Indigenous communities should NOT have any more say than anyone else about conservation and land use in their traditional territories.',
    statementB: 'Indigenous communities should have the final say about conservation and land use in their traditional territories.',
  },
  {
    id: 'Paired_OwnView4',
    statementA: 'Most people in this country are honest and can be trusted.',
    statementB: 'You really cannot trust anyone these days.',
  },
  {
    id: 'Paired_OwnView2',
    statementA: 'Sometimes the use of physical force is necessary in everyday life.',
    statementB: 'The use of physical force should be avoided at all costs in everyday life.',
  },
  {
    id: 'Paired_OwnView3',
    statementA: 'Human needs must always come before the needs of other living things.',
    statementB: 'Humans are a part of nature, not above it; all living things deserve equal respect.',
  },
] as const

// Response orders for left/right sides (divergent from center)
// Left side: Statement A (blue), Right side: Statement B (orange)
const RESPONSE_ORDER_A = ['2', '1'] as const  // Left bar: lean then strong (matches legend order)
const RESPONSE_ORDER_B = ['4', '5'] as const  // Right bar: light to dark (toward center)

// Blue gradient for Statement A side (LEFT bar)
const COLORS_A: Record<string, string> = {
  '1': '#4e79a7',   // Dark blue (strong A)
  '2': '#7eb0d5',   // Light blue (lean A)
}

// Red/orange gradient for Statement B side (RIGHT bar)
const COLORS_B: Record<string, string> = {
  '4': '#f28e2c',   // Light orange (lean B)
  '5': '#e15759',   // Red (strong B)
}

// Legend items
const LEGEND_A = [
  { label: '1 (Strong A)', color: '#4e79a7' },
  { label: '2 (Lean A)', color: '#7eb0d5' },
]

// Statement B bar is LTR, so 4 appears on left (near center), 5 on right
const LEGEND_B = [
  { label: '4 (Lean B)', color: '#f28e2c' },
  { label: '5 (Strong B)', color: '#e15759' },
]

export function PairedStatements() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Helper to get Statement A total percentage (responses 1 and 2)
  const getStatementAPercentage = (questionData: { responses: Array<{ label: string; percentage: number }> } | undefined) => {
    if (!questionData) return 0
    return questionData.responses
      .filter(r => ['1', '2'].includes(r.label))
      .reduce((sum, r) => sum + r.percentage, 0)
  }

  // Sort paired statements by Statement A support (highest first)
  const sortedStatements = useMemo(() => {
    if (!data) return PAIRED_STATEMENTS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...PAIRED_STATEMENTS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const aATotal = getStatementAPercentage(aData)
      const bATotal = getStatementAPercentage(bData)

      return bATotal - aATotal // Descending order (highest Statement A first)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Views and Opinions
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-4">
          For each pair of statements below, please indicate which one is closer to your own view on a scale of 1-5.
          (3 = Neutral/No opinion, not shown)
        </p>

        {/* Legend - Statement A first (left side), then B (right side) */}
        <div className="flex gap-x-8 mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Statement A:</span>
            {LEGEND_A.map(item => (
              <div key={`a-${item.label}`} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Statement B:</span>
            {LEGEND_B.map(item => (
              <div key={`b-${item.label}`} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Column headers - A on left, B on right */}
        <div className="flex items-center mb-4 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {isBreakActive && <div className="w-48 shrink-0" />}
          <div className="flex-1 text-right pr-4">Statement A</div>
          <div className="w-28 text-center shrink-0"></div>
          <div className="flex-1 text-left pl-4">Statement B</div>
          <div className="w-16 shrink-0" />
        </div>

        <div className="space-y-8">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedStatements.map(({ id, statementA, statementB }) => {
              const question = data.questions[id]
              if (!question) {
                return (
                  <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      Data not available for {id}
                    </p>
                  </div>
                )
              }

              // Build rows for each break value
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey(
                  'total',
                  selectedBreak,
                  isBreakActive ? value : undefined,
                  selectedProvince
                )
                let questionData = question.data[filterKey]

                // Fallback: try without province filter if no data found
                if (!questionData && selectedProvince) {
                  const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                  questionData = question.data[fallbackKey]
                }

                if (!questionData) return null

                // Get segments for Statement A side (1, 2)
                const getSegmentsA = () => {
                  return RESPONSE_ORDER_A.map(rank => {
                    const response = questionData!.responses.find(r => r.label === rank)
                    return { label: rank, percentage: response?.percentage || 0 }
                  }).filter(r => r.percentage > 0)
                }

                // Get segments for Statement B side (4, 5)
                const getSegmentsB = () => {
                  return RESPONSE_ORDER_B.map(rank => {
                    const response = questionData!.responses.find(r => r.label === rank)
                    return { label: rank, percentage: response?.percentage || 0 }
                  }).filter(r => r.percentage > 0)
                }

                return {
                  value,
                  filterKey,
                  segmentsA: getSegmentsA(),
                  segmentsB: getSegmentsB(),
                  sampleSize: questionData.sampleSize,
                }
              }).filter(row => row !== null)

              if (rows.length === 0) {
                return (
                  <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      No data for current filters
                    </p>
                  </div>
                )
              }

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                  {/* Statement labels above the chart - A on left, B on right */}
                  <div className="grid grid-cols-2 gap-8 mb-3">
                    <div className="text-xs text-slate-600 dark:text-slate-400 text-right">
                      <span className="font-semibold text-blue-600 dark:text-blue-400">A: </span>
                      {statementA}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 text-left">
                      <span className="font-semibold text-orange-600 dark:text-orange-400">B: </span>
                      {statementB}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row!.filterKey} className="flex items-center gap-2">
                        {isBreakActive && (
                          <div
                            className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0"
                            title={getDisplayName(row!.value)}
                          >
                            {getDisplayName(row!.value)}
                          </div>
                        )}
                        {/* Statement A (left bar) - grows toward center (right-to-left) - BLUE */}
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={row!.segmentsA}
                            scaleType="paired"
                            height={isBreakActive ? 24 : 32}
                            showLabels={false}
                            normalize={false}
                            direction="rtl"
                            colorGetter={(label) => COLORS_A[label] || '#4e79a7'}
                          />
                        </div>
                        {/* Center percentages - A% | B% */}
                        <div className="w-28 text-center shrink-0">
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {Math.round(row!.segmentsA.reduce((sum, s) => sum + s.percentage, 0))}%
                            <span className="mx-1 text-slate-300 dark:text-slate-600">|</span>
                            {Math.round(row!.segmentsB.reduce((sum, s) => sum + s.percentage, 0))}%
                          </span>
                        </div>
                        {/* Statement B (right bar) - grows from center outward - ORANGE/RED */}
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={row!.segmentsB}
                            scaleType="paired"
                            height={isBreakActive ? 24 : 32}
                            showLabels={false}
                            normalize={false}
                            colorGetter={(label) => COLORS_B[label] || '#e15759'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
